/*
 * R160ee · Feb 2026 — Activity-driven session keeper.
 *
 * Keeps the authenticated session alive as long as the user is
 * actually using the app. Every ~5 minutes we look at:
 *
 *   1. The last user-activity timestamp (mousedown / keydown / touch)
 *   2. Time remaining on the current JWT
 *
 * If the user has been active in the last `ACTIVITY_WINDOW_MS` AND the
 * token is within `REFRESH_LEEWAY_MS` of expiry, we call
 * `/auth/refresh` and swap in the new token. This produces a sliding
 * session: an active user never gets logged out mid-work, an idle one
 * still expires cleanly.
 *
 * The api client's own request interceptor is a safety-net for the
 * "silent" path when the user IS making calls; this keeper covers the
 * "just reading" case where no calls fire but the user is present.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";

// Oct 2026 · Backend JWT TTL dropped to 1 hour for the sliding session.
// Tighten the keeper so an active user ALWAYS gets a refresh:
//   • Tick every 2 min (was 5) so refresh fires promptly.
//   • "Active" if ANY input in the last 55 min — effectively a user
//     reading / typing throughout the hour never loses the session.
//   • Refresh when < 30 min remain on the current token.
// With a 1-hour token this means we hand out a new token roughly
// every 30 min for an active user; an idle user drops at the natural
// 1-hour mark.
const CHECK_INTERVAL_MS   = 2  * 60 * 1000;  // 2 min heartbeat
const ACTIVITY_WINDOW_MS  = 55 * 60 * 1000;  // treat as active if any input in last 55 min
const REFRESH_LEEWAY_MS   = 30 * 60 * 1000;  // refresh when < 30 min remaining
const ACTIVITY_STORAGE_KEY = "acq_last_activity_at";

function _decodeExpMs(token) {
  try {
    const b64 = token.split(".")[1];
    const json = JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof json.exp === "number") return json.exp * 1000;
  } catch { /* malformed */ }
  return 0;
}

export default function ActivitySessionKeeper() {
  const timerRef = useRef(null);
  const refreshingRef = useRef(false);

  // Record activity across tabs so a user typing in one tab keeps
  // the session in another idle tab alive too.
  useEffect(() => {
    const bump = () => {
      try { localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now())); } catch { /* ignore quota */ }
    };
    bump(); // initial mark so a page-load counts as activity
    const opts = { passive: true, capture: true };
    window.addEventListener("mousedown", bump, opts);
    window.addEventListener("keydown", bump, opts);
    window.addEventListener("touchstart", bump, opts);
    window.addEventListener("scroll", bump, opts);
    return () => {
      window.removeEventListener("mousedown", bump, opts);
      window.removeEventListener("keydown", bump, opts);
      window.removeEventListener("touchstart", bump, opts);
      window.removeEventListener("scroll", bump, opts);
    };
  }, []);

  useEffect(() => {
    const tick = async () => {
      if (refreshingRef.current) return;
      const token = localStorage.getItem("co_token");
      if (!token) return;

      const lastActivity = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY) || "0");
      if (!lastActivity || Date.now() - lastActivity > ACTIVITY_WINDOW_MS) return;

      const expMs = _decodeExpMs(token);
      if (!expMs || expMs <= Date.now()) return;               // dead — request interceptor will handle
      if (expMs - Date.now() > REFRESH_LEEWAY_MS) return;      // plenty of time left

      refreshingRef.current = true;
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newTok = res?.data?.token;
        if (newTok) {
          try { localStorage.setItem("co_token", newTok); } catch { /* ignore */ }
        }
      } catch { /* silent — user's next API call will handle 401 */ }
      finally { refreshingRef.current = false; }
    };
    // Kick off almost immediately so an active tab with a stale token
    // gets renewed on mount too, then every CHECK_INTERVAL_MS.
    const initial = setTimeout(tick, 5000);
    timerRef.current = setInterval(tick, CHECK_INTERVAL_MS);
    return () => { clearTimeout(initial); clearInterval(timerRef.current); };
  }, []);

  return null;
}
